import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { DevincoConnectUserData } from '@core/auth/interfaces';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvIconMenuItem, DvNavMenuItem } from '@devinco/ui/legacy';
import { EditProfileComponent } from '@features/edit-profile/edit-profile.component';
import { LanguageDialogComponent } from '@features/language-dialog/language-dialog.component';
import { LocalStorageItems } from '@shared/constants/local-storage-item';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { UserLogEventType } from '@shared/constants/user-log-event-type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserLogEventService } from '@shared/services/user-log-event.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { combineLatest, Subject, take } from 'rxjs';
import { UserProfileData } from './interfaces';
import { ReleaseNotesService } from '@features/release-notes-dialog/release-notes.service';
import { DialogService } from '@core/services/dialog.service';
import {
  DvIconMenuComponent,
  DvMenuItemParams,
  DvNavMenuComponent,
  DvToolbarComponent,
  DvToolbarMenuComponent,
} from '@devinco/ui/dds';
import { RouterOutlet } from '@angular/router';
import { FeaturesService } from '@core/services/features.service';
import { Feature } from '@shared/constants/feature';
import { ReleaseSyncService } from '@core/services/release-sync.service';
import { AsyncPipe } from '@angular/common';
import { helpCenterLink } from '@shared/components/release-notes-link/release-notes-links';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { DvAlertBannerComponent } from '@devinco/ui/dds/alert-banner';
import { RegistryComponentNames } from '@shared/constants/registry-component-name';
import { createCustomElement } from '@angular/elements';
import { PoiPopupContentComponent } from '@pages/route-network-page/components/poi-popup-content/poi-popup-content.component';
import { VippsPaymentsRouteName } from '@pages/vipps-payments/vipps-payments-routes';
import { RouteNetworkRouteName } from '@pages/route-network-page/route-network.routes';
import { OnCallRouteName } from '@pages/settings-page/settings-route-names';
import { EmissionsPageRouteName } from '@pages/emissions-page/emissions.routes';
import { DamageReportsRouteName } from '@pages/damage-reports-page/damage-reports.routes';
import { FleetRouteName } from '@pages/fleet/fleet.routes';
import { ReportsRouteName } from '@pages/reports-page/reports-page.module';
import { UserRouteName } from '@pages/users-page/users-page.module';
import { POIRouteName } from '@pages/poi-page/poi-page.module';
import { VehicleRouteName } from '@pages/vehicles-page/vehicles-page.module';
import { TripRouteName } from '@pages/trips-page/trips-page.module';
import { AssetRouteName } from '@pages/assets-page/assets-page.module';
import { StatisticsRouteName } from '@pages/statistics-page/statistics.module';

@Component({
  selector: 'app-main-layout',
  imports: [
    DvToolbarComponent,
    DvIconMenuComponent,
    DvNavMenuComponent,
    RouterOutlet,
    DvToolbarMenuComponent,
    TranslateModule,
    DvAlertBannerComponent,
    AsyncPipe,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent {
  readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly translate = inject(TranslateService);
  private readonly userRolesService = inject(UserRolesAndPermissionsService);
  private readonly api = inject(RestApiService);
  private readonly userLogEventService = inject(UserLogEventService);
  private releaseNotesService = inject(ReleaseNotesService);
  private dialog = inject(DialogService);
  private featuresService = inject(FeaturesService);
  private releaseSyncService = inject(ReleaseSyncService);
  private userMetaDataService = inject(UserMetadataService);

  iconMenuItems = signal<DvIconMenuItem[]>([]);
  navItems = signal<DvNavMenuItem[]>([]);
  userData = signal<DevincoConnectUserData | undefined>(undefined);

  isNavMenuOpen = signal<boolean>(false);
  menuItems = signal<DvMenuItemParams[]>([]);
  displayBannerTrigger$: Subject<boolean>;
  clientName = signal<string>(this.userMetaDataService.getClientName());

  constructor() {
    this.setupUserDataAndMenuItems();
    this.toggleNavMenuVisibility();
    this.checkReleaseNotes();
    this.startReleaseSync();

    const injector = inject(Injector);
    // do not register element if it's already defined
    if (!customElements.get(RegistryComponentNames.POiPopupContent)) {
      const VtPoiMapPopupElement = createCustomElement(
        PoiPopupContentComponent,
        {
          injector,
        }
      );
      // Register the custom element with the browser.
      customElements.define(
        RegistryComponentNames.POiPopupContent,
        VtPoiMapPopupElement
      );
    }
  }

  reloadApp = (): void => {
    this.releaseSyncService.reloadApp();
  };

  navMenuToggled(isOpen: boolean): void {
    localStorage.setItem(LocalStorageItems.NavMenuToggleKey, String(isOpen));
  }

  private setupUserDataAndMenuItems(): void {
    this.oidcSecurityService.userData$
      .pipe(take(1))
      .subscribe(({ userData }) => {
        this.userData.set(userData as DevincoConnectUserData);
        this.menuItems.set([
          { label: userData.name, subtext: userData.email },
          { divider: true },
          {
            icon: 'language',
            label: this.translate.instant('Language'),
            onClick: this.onLanguageClick,
          },
          {
            icon: 'account_circle',
            label: this.translate.instant('General.Edit.Profile'),
            onClick: this.onEditProfileClick,
          },
          { divider: true },
          {
            icon: 'version_information',
            label: this.translate.instant('General.VersionInformation'),
            onClick: () =>
              this.releaseNotesService.openVersionInformationDialog(),
          },
          {
            icon: 'speaker_notes',
            label: this.translate.instant('General.ReleaseNotes'),
            onClick: () => this.releaseNotesService.openReleaseNotesDialog(),
          },
          {
            icon: 'help_circle',
            label: this.translate.instant('General.HelpCenter'),
            isExternal: true,
            link: helpCenterLink,
          },
          { divider: true },
          {
            icon: 'teamviewer',
            label: this.translate.instant('General.RemoteControl'),
            link: 'https://get.teamviewer.com/devincosupport',
            isExternal: true,
          },
          { divider: true },
          {
            icon: 'exit_to_app',
            label: this.translate.instant('General.LogOut'),
            onClick: this.logout,
          },
        ]);
      });

    combineLatest([
      this.userRolesService.getUserRolesAndPermissions(),
      this.featuresService.getFeatures(),
    ])
      .pipe(take(1))
      .subscribe(([rolesAndPerms, features]) => {
        this.navItems.set(
          this.getNavItems(
            rolesAndPerms.Permissions,
            features,
            this.userMetaDataService.hasSkiDomain()
          )
        );
        this.iconMenuItems.set(
          this.getIconMenuItems(rolesAndPerms.Permissions)
        );
      });
  }

  private onLanguageClick = (): void => {
    this.dialog.open<undefined, LanguageDialogComponent>({
      title: 'Language.Select',
      confirmText: 'General.Save',
      cancelText: 'General.Cancel',
      contentComponent: LanguageDialogComponent,
      onConfirm: event => event.context.contentComponent?.performSave(),
    });
  };

  private getIconMenuItems = (permissions: string[]): DvIconMenuItem[] => {
    return [
      {
        clickFn: () => {
          const elem = document.getElementsByTagName('body')[0];
          if (!document.fullscreenElement) {
            elem.requestFullscreen();
          } else {
            document.exitFullscreen();
          }
        },
        icon: 'fullscreen',
        tooltip: this.translate.instant('Main.Fullscreen'),
      },
      {
        link: '/settings',
        icon: 'settings',
        tooltip: this.translate.instant('Main.Settings'),
        hide: !permissions.includes(RolesAndPermissions.SettingsRead),
      },
    ];
  };

  private getNavItems = (
    permissions: string[],
    features: Feature[],
    isSkiDomainEnabled = false
  ): DvNavMenuItem[] => {
    const navItems: DvNavMenuItem[] = [
      {
        icon: 'insert_chart_outlined',
        iconFont: 'material-icons-outlined',
        label: this.translate.instant('General.Statistics'),
        link: `/${StatisticsRouteName.Main}`,
        hide:
          !permissions.includes(RolesAndPermissions.StatisticsRead) ||
          !features.includes(Feature.Statistics),
      },
      {
        icon: 'road',
        label: this.translate.instant('General.Trips'),
        link: `/${TripRouteName.Main}`,
        hide:
          !permissions.includes(RolesAndPermissions.TripRead) ||
          !features.includes(Feature.Trips),
      },
      {
        icon: 'viatracks_find',
        label: this.translate.instant('General.Assets'),
        link: `/${AssetRouteName.Main}`,
        hide:
          !permissions.includes(RolesAndPermissions.AssetRead) ||
          !features.includes(Feature.Assets),
      },
      {
        icon: 'directions_car',
        iconFont: 'material-icons-outlined',
        label: this.translate.instant('General.Vehicles'),
        link: `/${VehicleRouteName.Main}`,
        hide:
          !permissions.includes(RolesAndPermissions.VehicleRead) ||
          !features.includes(Feature.Vehicles),
      },
      {
        icon: 'map_marker_radius',
        label: this.translate.instant('General.Poi'),
        link: `/${POIRouteName.Main}`,
        hide:
          !permissions.includes(RolesAndPermissions.POIRead) ||
          !features.includes(Feature.POI),
      },
      {
        icon: 'account_settings',
        label: this.translate.instant('General.Users'),
        link: `/${UserRouteName.Main}`,
        hide:
          !permissions.includes(RolesAndPermissions.UserRead) ||
          !features.includes(Feature.Users),
      },
      {
        icon: 'file_settings',
        label: this.translate.instant('General.Reports'),
        link: `/${ReportsRouteName.Main}`,
        hide:
          !permissions.includes(RolesAndPermissions.ReportRead) ||
          !features.includes(Feature.Reports),
      },
      {
        icon: 'car_crash',
        iconFont: 'material-icons-outlined',
        label: this.translate.instant('General.DamageReports'),
        link: `/${DamageReportsRouteName.Main}`,
        hide:
          !features.includes(Feature.DamageReport) ||
          !permissions.includes(RolesAndPermissions.DamageEdit) ||
          !permissions.includes(RolesAndPermissions.DamageRead),
      },
      {
        icon: 'map',
        label: this.translate.instant('General.Fleet'),
        link: `/${FleetRouteName.Main}`,
        hide:
          !features.includes(Feature.Fleet) ||
          !permissions.includes(RolesAndPermissions.FleetRead),
      },
      {
        icon: 'smoke',
        label: this.translate.instant('General.Emissions'),
        link: `/${EmissionsPageRouteName.Main}`,
        hide:
          !features.includes(Feature.Emissions) ||
          !permissions.includes(RolesAndPermissions.EmissionRead),
      },
      {
        icon: 'ring_volume',
        label: this.translate.instant('General.StandbyPersonnel'),
        link: `/${OnCallRouteName.Main}`,
        hide:
          !permissions.includes(RolesAndPermissions.StandbyRead) ||
          !features.includes(Feature.Standby),
      },
      {
        icon: 'draw_polyline',
        label: this.translate.instant('General.RouteNetworks'),
        link: `/${RouteNetworkRouteName.Main}`,
        hide: !isSkiDomainEnabled,
      },
      {
        icon: 'vipps',
        label: this.translate.instant('General.VippsPayments'),
        link: `/${VippsPaymentsRouteName.Main}`,
        hide: !isSkiDomainEnabled,
      },
    ];

    return navItems;
  };

  private logout = (): void => {
    this.userLogEventService.logEvent(UserLogEventType.UserLogout);
    this.oidcSecurityService.logoff().subscribe();
  };

  private onEditProfileClick = (): void => {
    combineLatest([
      // https://devinco.atlassian.net/browse/VW-2122
      // this.api.getCustom<boolean>(
      //   'customer/GetExternalApplicationState/VismaExpense'
      // ),
      this.api.getCustom<boolean>('user/GetExternalApplicationAccess'),
      this.api.getCustom<boolean | undefined>('vehicleVisibleInMap'),
    ])
      .pipe(take(1))
      .subscribe(([isConnectedToVisma, vehicleVisibleInMap]) => {
        const userData = this.userData();
        const profileData: Partial<UserProfileData> = {
          lang: userData?.lang,
          given_name: userData?.given_name,
          family_name: userData?.family_name,
          canConnectToVisma: false,
          isConnectedToVisma: !!isConnectedToVisma,
          vehicleVisibleInMap,
        };
        this.dialog.open<Partial<UserProfileData>, EditProfileComponent>({
          title: 'General.Edit.Profile',
          confirmText: 'General.Save',
          cancelText: 'General.Cancel',
          hideFooter: true,
          contentComponent: EditProfileComponent,
          data: profileData,
          size: 'lg',
        });
      });
  };

  private toggleNavMenuVisibility(): void {
    this.isNavMenuOpen.set(
      localStorage.getItem(LocalStorageItems.NavMenuToggleKey) === null ||
        localStorage.getItem(LocalStorageItems.NavMenuToggleKey) === 'true'
    );
  }

  private checkReleaseNotes(): void {
    this.releaseNotesService.checkForNewRelease();
  }

  private startReleaseSync(): void {
    this.releaseSyncService.setupReleaseSyncConnection();
    this.displayBannerTrigger$ = this.releaseSyncService.displayBannerTrigger$;
  }
}
